// Brücke zur Merxo-App (docs/mobile/SPEZIFIKATION.md §3): Die native Hülle (Capacitor 8) spielt `window.Capacitor`
// zu Dokumentbeginn ein; die Oberfläche bekommt bewusst KEIN @capacitor/core, denn sie läuft gegen beliebig alte
// App-Fassungen – die eingespielte Brücke passt immer zur App. Dies ist die EINZIGE Datei der Oberfläche, die
// `window.Capacitor` anfasst (Wächter-Test web/lib/native.guard.test.ts). Ohne App ist alles null bzw. ohne Wirkung.

export type NativePlatform = 'ios' | 'android'
/**
 * 'journal': natives Journal der Kasse ohne Verbindung (docs/kasse-offline/SPEZIFIKATION.md §14.6) ·
 * 'push': Push-Mitteilungen (docs/mobile/SPEZIFIKATION.md §12) – pushStatus, registerPush, unregisterPush,
 * openNotificationSettings
 */
export type NativeFeature = 'print' | 'shareFile' | 'haptic' | 'journal' | 'push'

/** CSS-Pixel relativ zum Viewport (getBoundingClientRect() des auslösenden Elements) – Anker für Popover auf dem iPad */
export interface NativeRect {
  x: number
  y: number
  width: number
  height: number
}

export interface NativeAppInfo {
  /** Hauptversion der Schnittstelle; ändert sich nur bei einem Bruch (soll nie passieren) */
  bridge: 1
  appVersion: string
  build: string
  platform: NativePlatform
  osVersion: string
  model: string
  formFactor: 'phone' | 'tablet'
  /** MERXO_SERVER, ohne abschließenden Schrägstrich */
  serverUrl: string
  debug: boolean
  /** was diese App-Fassung kann – vor jedem Aufruf prüfen */
  features: NativeFeature[]
}

export interface NativePrintOptions {
  /** Titel des Druckauftrags, z. B. 'Lieferschein LS-2026-4410' */
  jobName: string
  /** Standard 'A4' */
  pageSize?: 'A4' | 'A5' | 'receipt-80mm' | 'label'
  /** Standard 'portrait' */
  orientation?: 'portrait' | 'landscape'
  anchor?: NativeRect
}
export interface NativePrintResult {
  outcome: 'completed' | 'cancelled' | 'unknown'
}

export interface NativeShareFileOptions {
  /** mit Endung, z. B. 'artikel-2026-10-15.csv' */
  fileName: string
  /** 'text/csv' (Parameter wie ';charset=utf-8' erlaubt, werden abgeschnitten) */
  mimeType: string
  /** Standard-Base64 (RFC 4648), ohne 'data:'-Vorsatz, ohne Leerraum */
  base64: string
  /** Überschrift des Android-Dialogs; Standard: fileName */
  title?: string
  anchor?: NativeRect
}
export interface NativeShareFileResult {
  outcome: 'shared' | 'saved' | 'cancelled' | 'presented'
}

export type NativeHapticKind = 'success' | 'warning' | 'error' | 'selection'

// ---- Natives Journal (docs/kasse-offline/SPEZIFIKATION.md §9.1, §14.6; docs/mobile/SPEZIFIKATION.md §3.2/§3.3) ----
// Zweitspeicher der Kasse ohne Verbindung: eine Datei je Strom, nur anfügen, dauerhaft geschrieben vor dem Erfolg.

/** Kopf eines Stroms: höchste Folgenummer und ihr Prüfwert */
export interface NativeJournalHead {
  seq: number
  hash: string
}
/** Eine Zeile des Journals (line = JSON des Offline-Satzes mit payload/hash/sig; ohne Zeilenumbruch) */
export interface NativeJournalEntry {
  seq: number
  hash: string
  line: string
}
export interface NativeJournalAppendOptions {
  /** `${tenantId}:${registerId}:${deviceId}` – Buchstaben, Ziffern, „.“, „_“, „:“, „-“, höchstens 120 Zeichen */
  stream: string
  /** genau Kopf + 1, sonst INVALID_ARGUMENT; ein neuer Strom beginnt mit dem ersten Satz (jede Folgenummer ≥ 1) */
  seq: number
  hash: string
  /** höchstens NATIVE_JOURNAL_MAX_LINE_BYTES in UTF-8, sonst TOO_LARGE */
  line: string
}
export interface NativeJournalReadOptions {
  stream: string
  /** ab dieser Folgenummer (einschließlich) */
  fromSeq: number
  /** 1–1000; die App liefert weniger, wenn die Zeilen zusammen mehr als 8 MiB hätten – dann ab der letzten weiterlesen */
  limit: number
}
export interface NativeJournalReadResult {
  entries: NativeJournalEntry[]
  /** Kopf des Stroms (auch wenn entries leer ist); null, wenn es den Strom nicht gibt */
  head: NativeJournalHead | null
}
export interface NativeJournalStream {
  stream: string
  head: NativeJournalHead | null
}
export interface NativeJournalTrimOptions {
  stream: string
  /** Sätze bis einschließlich dieser Folgenummer entfernen – den Kopf (höchste Folgenummer) behält die App immer */
  uptoSeq: number
}

/** Methoden der Brücke – nur vorhanden, wenn appInfo().features 'journal' enthält. */
export interface MerxoNativeJournalMethods {
  journalAppend(options: NativeJournalAppendOptions): Promise<{ ok: true }>
  journalRead(options: NativeJournalReadOptions): Promise<NativeJournalReadResult>
  /** Capacitor liefert nur Objekte – die Liste steckt in `streams` */
  journalStreams(): Promise<{ streams: NativeJournalStream[] }>
  journalTrim(options: NativeJournalTrimOptions): Promise<{ removed: number }>
}

/** So reicht @ui das Journal an die Kasse weiter (modules/pos/web/offline/journal.ts, `NativeJournalPort`). */
export interface NativeJournalPort {
  journalAppend(options: NativeJournalAppendOptions): Promise<{ ok: true }>
  journalRead(options: NativeJournalReadOptions): Promise<NativeJournalReadResult>
  journalStreams(): Promise<NativeJournalStream[]>
  journalTrim(options: NativeJournalTrimOptions): Promise<{ removed: number }>
}

/** Höchstlänge einer Zeile des Journals: 300 KiB in UTF-8 (docs/kasse-offline/SPEZIFIKATION.md §14.6). */
export const NATIVE_JOURNAL_MAX_LINE_BYTES = 307_200

// ---- Push-Mitteilungen (docs/mobile/SPEZIFIKATION.md §12) ----------------------------------------------------------

/** Erlaubnis im System: noch nicht gefragt (prompt), erteilt (granted) oder abgelehnt bzw. in den Einstellungen aus (denied) */
export type NativePushPermission = 'prompt' | 'granted' | 'denied'
/** APNs-Umgebung der App: Debug-Builds 'sandbox', Store/TestFlight 'production'; Android immer 'production' */
export type NativePushEnvironment = 'sandbox' | 'production'

export interface NativePushStatus {
  /** false: Diese App-Fassung kann keine Mitteilungen empfangen (Android ohne Firebase gebaut) */
  available: boolean
  permission: NativePushPermission
  /** zuletzt vom System gemeldetes Token (APNs als Hex in Kleinbuchstaben) – null, wenn noch nie angemeldet */
  token: string | null
  environment: NativePushEnvironment
}

export interface NativePushRegistration {
  permission: NativePushPermission
  /** null, wenn die Erlaubnis fehlt */
  token: string | null
  environment: NativePushEnvironment
}

/** Methoden der Brücke – nur vorhanden, wenn appInfo().features 'push' enthält. */
export interface MerxoNativePushMethods {
  pushStatus(): Promise<NativePushStatus>
  /** fragt beim ersten Mal die Erlaubnis an, meldet beim Mitteilungsdienst an und liefert das Token */
  registerPush(): Promise<NativePushRegistration>
  unregisterPush(): Promise<{ ok: true }>
  /** Systemeinstellungen der App öffnen (Mitteilungen) */
  openNotificationSettings(): Promise<void>
}

/** So reicht @ui die Push-Brücke an Profil und Hülle weiter. */
export interface NativePushPort {
  platform: NativePlatform
  status(): Promise<NativePushStatus>
  register(): Promise<NativePushRegistration>
  unregister(): Promise<void>
  openSettings(): Promise<void>
}

export interface MerxoNativePlugin extends Partial<MerxoNativeJournalMethods>, Partial<MerxoNativePushMethods> {
  appInfo(): Promise<NativeAppInfo>
  print(options: NativePrintOptions): Promise<NativePrintResult>
  shareFile(options: NativeShareFileOptions): Promise<NativeShareFileResult>
  haptic(options: { kind: NativeHapticKind }): Promise<void>
}

/** Fehler kommen als Ablehnung mit { code, message }; message ist deutsch und darf angezeigt werden. */
export type NativeErrorCode =
  | 'INVALID_ARGUMENT' // Pflichtfeld fehlt, Base64 kaputt, mimeType ungültig, leere Datei
  | 'TOO_LARGE' // Datei > 25 MiB (26 214 400 Byte) nach dem Dekodieren
  | 'BUSY' // ein Druck- oder Teilen-Dialog ist schon offen
  | 'UNAVAILABLE' // Drucken auf dem Gerät nicht möglich
  | 'FORBIDDEN_ORIGIN' // Aufruf von einer Seite, deren Herkunft nicht MERXO_SERVER ist
  | 'FAILED' // alles andere (Text nennt die Ursache)

/** Größenbremse der Brücke: 25 MiB nach dem Dekodieren (§3.2). */
export const NATIVE_MAX_FILE_BYTES = 26_214_400

const ERROR_CODES: readonly NativeErrorCode[] = ['INVALID_ARGUMENT', 'TOO_LARGE', 'BUSY', 'UNAVAILABLE', 'FORBIDDEN_ORIGIN', 'FAILED']

/** Abgelehnter Aufruf der Brücke mit Code und (deutscher) Meldung der App. */
export class NativeError extends Error {
  readonly code: NativeErrorCode
  constructor(code: NativeErrorCode, message: string) {
    super(message)
    this.name = 'NativeError'
    this.code = code
  }
}

/** Ablehnung der Brücke (Capacitor: { code, message } bzw. CapacitorException) → NativeError. */
export function toNativeError(cause: unknown): NativeError {
  if (cause instanceof NativeError) return cause
  const raw = (cause ?? {}) as { code?: unknown; message?: unknown }
  const code = typeof raw.code === 'string' && (ERROR_CODES as readonly string[]).includes(raw.code) ? (raw.code as NativeErrorCode) : 'FAILED'
  const message = typeof raw.message === 'string' && raw.message.trim() ? raw.message : typeof cause === 'string' ? cause : ''
  return new NativeError(code, message)
}

// ---- Erkennung (§3.1) -----------------------------------------------------------------------------------

/** User-Agent-Zusatz der App: `MerxoApp/<version> (ios|android)` */
export const APP_UA = /\bMerxoApp\/(\d+\.\d+\.\d+) \((ios|android)\)/

/** Das, was Capacitor 8 zu Dokumentbeginn einspielt – nur der Teil, den Merxo braucht. */
interface CapacitorBridge {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
  isPluginAvailable?: (name: string) => boolean
  Plugins?: Record<string, unknown>
}

export interface NativeApp {
  platform: NativePlatform
  /** Fassung aus dem User-Agent, z. B. '0.1.0' */
  version: string
}

const bridge = (): CapacitorBridge | undefined =>
  typeof window === 'undefined' ? undefined : (window as unknown as { Capacitor?: CapacitorBridge }).Capacitor

/**
 * In der App gilt genau dann: User-Agent-Zusatz UND `Capacitor.isNativePlatform()` UND Plugin `MerxoNative`.
 * Nur der User-Agent ohne Brücke (Fehler beim Einspielen) heißt: nicht in der App → Browserverhalten.
 */
export function detectNativeApp(userAgent: string | undefined, capacitor: CapacitorBridge | undefined): NativeApp | null {
  const match = APP_UA.exec(userAgent ?? '')
  if (!match || !capacitor) return null
  try {
    if (capacitor.isNativePlatform?.() !== true) return null
    if (capacitor.isPluginAvailable?.('MerxoNative') !== true) return null
  } catch {
    return null
  }
  return { version: match[1] as string, platform: match[2] as NativePlatform }
}

const detectNow = (): NativeApp | null =>
  detectNativeApp(typeof navigator === 'undefined' ? undefined : navigator.userAgent, bridge())

// synchron beim Laden des Moduls bestimmt und gemerkt (die Brücke steht vor dem ersten Skript)
let detected: NativeApp | null = detectNow()
let info: Promise<NativeAppInfo | null> | null = null

/** In der Merxo-App? Synchron; `null` im Browser. */
export function nativeApp(): NativeApp | null {
  return detected
}

/** Plattform der Merxo-App ('ios' | 'android') – `null` im Browser (gleiche Erkennung wie nativeApp, §3.1). */
export function nativePlatform(): NativePlatform | null {
  return detected?.platform ?? null
}

/** Nur für Tests: Erkennung nach dem Fälschen von User-Agent/Brücke neu ausführen. */
export function resetNativeApp(): NativeApp | null {
  detected = detectNow()
  info = null
  return detected
}

function plugin(): MerxoNativePlugin | null {
  if (!detected) return null
  const candidate = bridge()?.Plugins?.MerxoNative as MerxoNativePlugin | undefined
  return candidate ?? null
}

/** `appInfo()` der App – einmal gefragt und gemerkt; `null` im Browser oder wenn die App nicht antwortet. */
export function nativeInfo(): Promise<NativeAppInfo | null> {
  const native = plugin()
  if (!native) return Promise.resolve(null)
  info ??= Promise.resolve()
    .then(() => native.appInfo())
    .then((value) => (value && Array.isArray(value.features) ? value : { ...value, features: [] }))
    .catch(() => null)
  return info
}

/** Kann diese App-Fassung das? (§3.7: vor jedem Aufruf prüfen) */
export async function nativeSupports(feature: NativeFeature): Promise<boolean> {
  return (await nativeInfo())?.features.includes(feature) ?? false
}

/** Anker eines Elements für Popover auf dem iPad. */
export function nativeRect(element: Element | null | undefined): NativeRect | undefined {
  if (!element || typeof element.getBoundingClientRect !== 'function') return undefined
  const rect = element.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return undefined
  return { x: Math.round(rect.left), y: Math.round(rect.top), width: Math.round(rect.width), height: Math.round(rect.height) }
}

/**
 * Druckt das aktuelle Dokument mit Druck-CSS über den System-Druckdialog.
 * `null`: nicht in der App oder die App-Fassung kann es nicht. Ablehnungen kommen als NativeError.
 */
export async function nativePrint(options: NativePrintOptions): Promise<NativePrintResult | null> {
  const native = plugin()
  if (!native || !(await nativeSupports('print'))) return null
  try {
    return await native.print(options)
  } catch (cause) {
    throw toNativeError(cause)
  }
}

/**
 * Übergibt eine Datei ans System (Teilen-Blatt bzw. Dialog „Teilen/Speichern“).
 * `null`: nicht in der App oder die App-Fassung kann es nicht. Ablehnungen kommen als NativeError.
 */
export async function nativeShareFile(options: NativeShareFileOptions): Promise<NativeShareFileResult | null> {
  const native = plugin()
  if (!native || !(await nativeSupports('shareFile'))) return null
  try {
    return await native.shareFile(options)
  } catch (cause) {
    throw toNativeError(cause)
  }
}

/** Haptische Rückmeldung (ersetzt in der App `navigator.vibrate`). Nie ein Fehler, im Browser ohne Wirkung. */
export function nativeHaptic(kind: NativeHapticKind): void {
  const native = plugin()
  if (!native) return
  void nativeSupports('haptic')
    .then((supported) => (supported ? native.haptic({ kind }) : undefined))
    .catch(() => undefined)
}

const journalHeadOf = (value: unknown): NativeJournalHead | null => {
  const head = value as { seq?: unknown; hash?: unknown } | null | undefined
  return head && typeof head.seq === 'number' && typeof head.hash === 'string' ? { seq: head.seq, hash: head.hash } : null
}

/**
 * Natives Journal der App (docs/kasse-offline/SPEZIFIKATION.md §14.6). `null` im Browser oder wenn die App-Fassung
 * 'journal' nicht kann. Ablehnungen der Brücke (falsche Folgenummer → INVALID_ARGUMENT, Zeile zu groß → TOO_LARGE,
 * fremde Herkunft → FORBIDDEN_ORIGIN, Schreibfehler → FAILED) kommen als geworfener NativeError. Ein Zeitlimit setzt
 * der Aufrufer selbst.
 */
export async function nativeJournalPort(): Promise<NativeJournalPort | null> {
  const native = plugin()
  if (!native || !(await nativeSupports('journal'))) return null
  const { journalAppend, journalRead, journalStreams, journalTrim } = native
  if (!journalAppend || !journalRead || !journalStreams || !journalTrim) return null
  const guarded = async <T>(run: () => Promise<T>): Promise<T> => {
    try {
      return await run()
    } catch (cause) {
      throw toNativeError(cause)
    }
  }
  return {
    journalAppend: (options) =>
      guarded(async () => {
        await journalAppend.call(native, options)
        return { ok: true as const }
      }),
    journalRead: (options) =>
      guarded(async () => {
        const value = (await journalRead.call(native, options)) as Partial<NativeJournalReadResult> | null
        const entries = Array.isArray(value?.entries) ? value.entries : []
        return {
          entries: entries.map((entry) => ({ seq: entry.seq, hash: entry.hash, line: entry.line })),
          head: journalHeadOf(value?.head),
        }
      }),
    journalStreams: () =>
      guarded(async () => {
        const value = (await journalStreams.call(native)) as { streams?: unknown } | null
        const streams = Array.isArray(value?.streams) ? (value.streams as NativeJournalStream[]) : []
        return streams.map((entry) => ({ stream: entry.stream, head: journalHeadOf(entry.head) }))
      }),
    journalTrim: (options) =>
      guarded(async () => {
        const value = (await journalTrim.call(native, options)) as { removed?: unknown } | null
        return { removed: typeof value?.removed === 'number' ? value.removed : 0 }
      }),
  }
}

const pushPermissionOf = (value: unknown): NativePushPermission => (value === 'granted' || value === 'denied' ? value : 'prompt')
const pushEnvironmentOf = (value: unknown): NativePushEnvironment => (value === 'sandbox' ? 'sandbox' : 'production')
const pushTokenOf = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null)

/**
 * Push-Brücke der App (docs/mobile/SPEZIFIKATION.md §12). `null` im Browser oder wenn die App-Fassung 'push' nicht
 * kann. Ablehnungen der Brücke (UNAVAILABLE: ohne Firebase gebaut, FAILED) kommen als geworfener NativeError.
 */
export async function nativePushPort(): Promise<NativePushPort | null> {
  const native = plugin()
  if (!native || !detected || !(await nativeSupports('push'))) return null
  const { pushStatus, registerPush, unregisterPush, openNotificationSettings } = native
  if (!pushStatus || !registerPush || !unregisterPush) return null
  const guarded = async <T>(run: () => Promise<T>): Promise<T> => {
    try {
      return await run()
    } catch (cause) {
      throw toNativeError(cause)
    }
  }
  return {
    platform: detected.platform,
    status: () =>
      guarded(async () => {
        const value = (await pushStatus.call(native)) as Partial<NativePushStatus> | null
        return {
          available: value?.available !== false,
          permission: pushPermissionOf(value?.permission),
          token: pushTokenOf(value?.token),
          environment: pushEnvironmentOf(value?.environment),
        }
      }),
    register: () =>
      guarded(async () => {
        const value = (await registerPush.call(native)) as Partial<NativePushRegistration> | null
        return { permission: pushPermissionOf(value?.permission), token: pushTokenOf(value?.token), environment: pushEnvironmentOf(value?.environment) }
      }),
    unregister: () =>
      guarded(async () => {
        await unregisterPush.call(native)
      }),
    openSettings: () =>
      guarded(async () => {
        await openNotificationSettings?.call(native)
      }),
  }
}

/**
 * Tippen auf eine Mitteilung: Die App ruft `window.merxoOpenPath(pfad)` (nur Pfade auf dem eigenen Server) und lädt
 * die Seite selbst, solange die Funktion fehlt. Gibt eine Aufräumfunktion zurück.
 */
export function registerNativeOpenPath(handler: (path: string) => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  const target = window as unknown as { merxoOpenPath?: (path: string) => string }
  const safe = (path: string): string => {
    if (typeof path !== 'string' || !/^\/(?!\/)/.test(path) || /[\s\\]/.test(path)) return 'ignored'
    try {
      handler(path)
      return 'ok'
    } catch {
      return 'error'
    }
  }
  target.merxoOpenPath = safe
  return () => {
    if (target.merxoOpenPath === safe) delete target.merxoOpenPath
  }
}

/**
 * Stil der Systemleisten (§3.5, Kern-Plugin SystemBars): 'DARK' = helle Symbole für dunklen Grund,
 * 'LIGHT' = dunkle Symbole für hellen Grund. Im Browser ohne Wirkung.
 */
export function setNativeBarStyle(style: 'DARK' | 'LIGHT'): void {
  if (!detected) return
  const bars = bridge()?.Plugins?.SystemBars as { setStyle?: (options: { style: string }) => Promise<void> } | undefined
  try {
    void bars?.setStyle?.({ style })?.catch?.(() => undefined)
  } catch {
    // ältere Hülle ohne SystemBars: die Leisten bleiben im Systemstil
  }
}

// ---- Zurück-Taste (§3.4, nur Android) ----------------------------------------------------------------------

export type BackResult = 'handled' | 'root' | 'default'

/** Registriert `window.merxoHandleBack` (die App ruft sie per evaluateJavascript). Gibt eine Aufräumfunktion zurück. */
export function registerNativeBack(handler: () => BackResult): () => void {
  if (typeof window === 'undefined') return () => undefined
  const target = window as unknown as { merxoHandleBack?: () => BackResult }
  const safe = (): BackResult => {
    try {
      return handler()
    } catch {
      return 'default'
    }
  }
  target.merxoHandleBack = safe
  return () => {
    if (target.merxoHandleBack === safe) delete target.merxoHandleBack
  }
}
